<template>
  <AccordionCard :title="$t('products.filters.brands')">
    <div class="brand-filter-content">
      <UiInput
        :value="searchInput"
        type="filter"
        name="searchInput"
        :placeholder="$t('products.filters.searchBrands')"
        class="brand-search"
        :is-disabled="isSearchDisabled"
        @input="onInputChange"
      />
      <div class="swa-checkbox-container">
        <UiCheckbox
          v-for="(brand, index) in brandOptions"
          :id="brand.id"
          :key="brand.id"
          :model-value="selectedFilterParams.brands"
          class="checkbox-option"
          :native-value="brand.slugUrl"
          :label="brand.name"
          @update:model-value="onFilterUpdate($event, brand, index)"
        />
      </div>
    </div>
  </AccordionCard>
</template>

<script lang="ts" setup>
import UiInput from '@sephora-asia/ui-input/UiInput.vue'
import UiCheckbox from '@sephora-asia/ui-checkbox'
import AccordionCard from './accordion-card.vue'
import type { BrandFilters } from '~/types/api/data/product/filter/filter.types'
import { toNormalizeForm } from '~/utils/string-helpers'
import useFilterTrackingEvent from '~/composables/utils/product-listing/filter/use-product-listing-filter-tracking'

const props = defineProps<{
  filterData: BrandFilters[]
  isResetted: boolean
}>()
const emit = defineEmits<{
  (e: 'resetted'): void
}>()

const { selectedFilterParams } = useProductListingFiltersStore()

const { trackFilterClicked } = useFilterTrackingEvent()

const searchInput = ref<string>('')

const isSearchDisabled = computed(() => props.filterData.length === 0)

const brandOptions = computed(() => {
  if (!searchInput.value) {
    return props.filterData
  }
  return filterBySearch()
})

function onInputChange(input: string | number) {
  if (typeof input === 'string') {
    searchInput.value = input
  }
}

function filterBySearch() {
  return props.filterData.filter((brand: BrandFilters) =>
    toNormalizeForm(brand.name)
      .toLowerCase()
      .includes(toNormalizeForm(searchInput.value).toLowerCase().trim())
  )
}

function onFilterUpdate(event: string[], brand: BrandFilters, index: number) {
  trackFilterClicked(event, {
    value: brand.name,
    slugUrl: brand.slugUrl,
    filterIndex: index,
    filterCategoryIndex: 0,
    type: 'brand',
  })
}

watch(
  () => props.isResetted,
  (isResetted: boolean) => {
    if (isResetted) {
      searchInput.value = ''
      emit('resetted')
    }
  }
)
</script>
<style lang="scss" scoped>
.brand-filter-content {
  .brand-search {
    margin-bottom: 16px;
  }
}
</style>
